import React, { useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Pause, Play } from 'lucide-react';
import { JobRoutine, ServiceStatusBadge, apiErrorMessage, formatRelativeTime } from '../services/servicesShared';

const KIND_LABELS: Record<string, string> = { job: 'Full wake', code: 'Script', agent: 'Agent' };

/** This job's routines (heartbeat, event subscriptions, scripts) with pause/resume. */
export const JobRoutines: React.FC<{
    jobId: string;
    routines: JobRoutine[];
    onChanged: (routine: JobRoutine) => void;
}> = ({ jobId, routines, onChanged }) => {
    const { post } = useApi();
    const [busy, setBusy] = useState<string | null>(null);
    const visible = routines.filter((routine) => routine.status !== 'removed');

    const setStatus = async (routine: JobRoutine, status: 'active' | 'paused') => {
        setBusy(routine.id);
        try {
            const response = await post(`/jobs/${jobId}/routines/${routine.id}/status`, { status });
            onChanged(response.data);
            toast.success(status === 'paused' ? 'Routine paused' : 'Routine resumed');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not update the routine'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div>
            <h3 className="text-lg font-semibold mb-3">Routines</h3>
            {visible.length ? (
                <div className="rounded-lg border divide-y">
                    {visible.map((routine) => (
                        <div key={routine.id} className="flex items-center justify-between gap-3 px-3 py-2">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium font-mono truncate">{routine.name}</span>
                                    <ServiceStatusBadge status={routine.status} />
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {KIND_LABELS[routine.kind] || routine.kind} · {routine.trigger_label}
                                    {routine.status === 'active' && routine.next_run_at ? ` · next ${formatRelativeTime(routine.next_run_at)}` : ''}
                                    {routine.last_run_at ? ` · last ${formatRelativeTime(routine.last_run_at)}` : ''}
                                    {routine.from_version ? ` · from v${routine.from_version}` : ' · added by the job'}
                                </div>
                                {routine.status_reason && (
                                    <div className="text-xs text-amber-600 dark:text-amber-400 truncate">{routine.status_reason}</div>
                                )}
                            </div>
                            {routine.status === 'active' ? (
                                <Button variant="ghost" size="sm" disabled={busy === routine.id} onClick={() => setStatus(routine, 'paused')}>
                                    <Pause className="h-3.5 w-3.5 mr-1" /> Pause
                                </Button>
                            ) : (
                                <Button variant="ghost" size="sm" disabled={busy === routine.id} onClick={() => setStatus(routine, 'active')}>
                                    <Play className="h-3.5 w-3.5 mr-1" /> Resume
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">No routines. The job runs only when something wakes it.</p>
            )}
        </div>
    );
};

export default JobRoutines;
